import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';

import Store from 'lib/Store';
import mapReducerActions from 'utils/mapReducerActions';

import config from 'config/general';

import getUserLogged from 'api/users/getUserLogged';

import initialState from './initialState';
import * as UserReducer from './reducer';

interface IUseUserProviderProps {
  children: React.ReactNode;
}

const store = Store(`${config.localStorageKey}-user`);

const UseUserContext = createContext<IUseUserContextData>({} as IUseUserContextData);

const UseUserProvider: React.FC<IUseUserProviderProps> = ({ children }) => {
  // # Reducer
  const initialData: IUser = store.get() || initialState;
  const [state, dispatch] = useReducer(UserReducer.reducer, { ...initialData });
  const actions: IUseUserActionsType = useMemo(
    () => mapReducerActions(UserReducer.actions, dispatch),
    [],
  );
  const reducer: IUseUserReducerType = useMemo(() => ({ state, actions }), [actions, state]);

  // # Ao atualizar o state, atualiza a store
  useEffect(() => {
    if (reducer.state.cacheKey) store.set(reducer.state);
  }, [reducer.state]);

  // # Clear
  const clear = useCallback(() => {
    store.clear();
    reducer.actions.setClear();
  }, [reducer.actions]);

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  // Salva as informações do usuário
  const saveUserDataOnReducer = useCallback(
    (data: Partial<IUser>) => {
      // Se tem dados de usuário, pode fechar o modal

      reducer.actions.setProfile(data);
    },
    [reducer.actions],
  );

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  // Salva as informações do usuário
  const signIn = useCallback(
    async (token: string): Promise<any> => {
      const userData = await getUserLogged(token);

      if (userData) {
        saveUserDataOnReducer(userData);
      }
    },
    [saveUserDataOnReducer],
  );

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const isUserLoggedIn = useMemo(
    () => !!(reducer.state.cacheKey && reducer.state.cacheKey !== ''),
    [reducer.state.cacheKey],
  );

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const contextValue = useMemo(
    () => ({
      ...reducer.state,
      ...reducer.actions,

      clear,

      signIn,

      saveUserDataOnReducer,
      isUserLoggedIn,
    }),
    [clear, signIn, isUserLoggedIn, reducer.actions, reducer.state, saveUserDataOnReducer],
  );

  return <UseUserContext.Provider value={contextValue}>{children}</UseUserContext.Provider>;
};

const useUser = (): IUseUserContextData => {
  const context = useContext(UseUserContext);
  if (!context) throw new Error('useUser must be used within an UseUserProvider');
  return context;
};

export { UseUserProvider, useUser };
